/* eslint-disable @typescript-eslint/indent */
import { BuyNowContract, ContractStatus, RealizationDetails, VehicleTypes, ListOffer } from 'dealer-types'
import React, { createContext, useReducer, ReactNode, Dispatch } from 'react'
interface ExternalState {
  label1: string
  label2: string
  buy_now_contract: BuyNowContract
  photo: string | null
  contract_status: ContractStatus
  is_liked: boolean
  id: number
  score: number
  index: number
  offer_hash_id: string
  created_at: string
  file_url: string
  year: string

  current_dealer_highest_bet: number | null
  published_at: string
  archived_at: string | null
  total_mileage: number
  region: string
  bets_count: number
  highest_bet: number
  minimal_price: number | null
  vehicle_type: VehicleTypes
  realization_details: RealizationDetails
  display_number: string
  final_price?: number
}
interface State extends ExternalState {
  expandedView: boolean
  bar: BarType | null
}

export type BarType =
  | 'buy_now'
  | 'auction_contract'
  | 'thank_you_auction_contract'
  | 'thank_you_transport'
  | 'thank_you_buy_now'
  | 'transport'
  | 'pick_up'
  | 'thank_you_pick_up'

interface CreateBetSuccessActionType {
  type: 'CREATE_BET_SUCCESS'
  payload: ListOffer
}

interface SetBarActionType {
  type: 'SET_BAR'
  payload: BarType
}

interface NoPayloadActionType {
  type:
    | 'CREATE_BET_FAILURE'
    | 'EXPAND_VIEW'
    | 'TOGGLE_VIEW'
    | 'RESET_BAR'
    | 'ACCEPT_BUY_NOW_CONTRACT_FAILURE'
    | 'ACCEPT_BUY_NOW_CONTRACT_SUCCESS'
    | 'CREATE_TRANSPORT_SUCCESS'
    | 'CREATE_TRANSPORT_FAILURE'
    | 'CONFIRM_PICK_UP_SUCCESS'
    | 'CONFIRM_PICK_UP_FAILURE'
    | 'TOGGLE_LIKED_OFFER_SUCCESS'
    | 'TOGGLE_LIKED_OFFER_FAILURE'
}

type ActionType = SetBarActionType | CreateBetSuccessActionType | NoPayloadActionType

interface ItemProviderProps extends ExternalState {
  children: ReactNode
}

const itemReducer = (state: State, action: ActionType): State => {
  switch (action.type) {
    case 'TOGGLE_VIEW':
      return { ...state, expandedView: !state.expandedView }
    case 'EXPAND_VIEW':
      return { ...state, expandedView: true }
    case 'SET_BAR':
      if (state.bar === 'thank_you_buy_now') return { ...state }
      return { ...state, bar: action.payload }
    case 'RESET_BAR':
      return { ...state, bar: state.buy_now_contract?.price > 0 ? 'buy_now' : 'auction_contract' }
    case 'CREATE_BET_SUCCESS': {
      return {
        ...state,
        bar: 'thank_you_auction_contract',
        ...action.payload
      }
    }
    case 'ACCEPT_BUY_NOW_CONTRACT_SUCCESS': {
      return { ...state, bar: 'thank_you_buy_now' }
    }
    case 'CREATE_TRANSPORT_SUCCESS': {
      return {
        ...state,
        bar: 'thank_you_transport',
        realization_details: { ...state.realization_details, spedition_arranged: true }
      }
    }
    case 'CONFIRM_PICK_UP_SUCCESS': {
      return { ...state, bar: 'thank_you_pick_up' }
    }
    case 'TOGGLE_LIKED_OFFER_SUCCESS': {
      return { ...state, is_liked: !state.is_liked }
    }

    default:
      return state
  }
}

export const ItemContext = createContext<State | null>(null)
export const ItemDispatchContext = createContext<Dispatch<ActionType> | null>(null)
const getBar = (offer: ListOffer): BarType => {
  if (typeof offer.bar === 'string') return offer.bar as BarType
  if (typeof offer.realization_details === 'object') {
    if (offer.realization_details.invoice_created) return 'thank_you_pick_up'
    return 'pick_up'
  }
  if (typeof offer.buy_now_contract?.price === 'number') {
    return offer.buy_now_contract.price > 0 ? 'buy_now' : 'auction_contract'
  }
  return 'auction_contract'
}

export function ItemProvider({ children, ...other }: ItemProviderProps): JSX.Element {
  const initialState: State = {
    // if height of element is big it suggest that on previous render element  has ben opened, so we want to render it open again
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore
    expandedView: other.lastHeight > 140,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore
    bar: getBar(other as ListOffer),
    ...other
  }

  const [state, dispatch] = useReducer(itemReducer, initialState)

  return (
    <ItemContext.Provider value={state}>
      <ItemDispatchContext.Provider value={dispatch}>{children}</ItemDispatchContext.Provider>
    </ItemContext.Provider>
  )
}
